import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ColumnShape } from 'react-base-table';
import { cloneDeep, flatMap, fromPairs, isEmpty, isEqual } from 'lodash';
import classnames from 'classnames';

import { useGetMappedEventsWithOutOfBox } from '@api/emp/queryHooks';
import { useGetEventNameById } from '@hooks/useGetEventNameById';
import { useDeploy, useMetricManagementList, useUndeploy } from '@src/emp-3.0/api/queryHooks';
import {
  useDeleteMetric,
  useGetMetricById,
  useDeleteVersion,
} from '@components/metrics-builder/api/queryHooks';
import { DeleteVersionType } from '@src/emp-3.0/components/Management/components/BulkAction/typed';
import { generateSelectOptions } from '@src/emp-3.0/utils/buildOptions';
import { IMetricsList } from '@src/emp-3.0/domain/metrics-template/types';
import { metricTypeOptions, statusOptions } from '@src/emp-3.0/utils/constants';
import { EFormMode } from '@src/emp-3.0/types/common';
import { useCreatePermissions } from '@hooks/usePermissions';
import {
  DEFAULT_SELECT_VALUE,
  dateFields,
  handleAction,
  supportSearchFields,
  METRICS,
} from '@src/emp-3.0/constants/management';
import { getFilteredSortedData } from '@src/emp-3.0/utils/management';
import { isDraftVersion, isMultiVersion } from '@src/emp-3.0/utils';
import { metricTypeMapping } from '@src/emp-3.0/components/DeploymentDrawer/constants';

import {
  useMetricsManagementStore,
  ManagementTable,
  generateColumn,
  managementTableStyles,
  SearchFilterPanel,
  BulkAction,
} from '../';
import { useWorkflowStore } from '@src/pages/workflow/store';
import { workbenchModeTrueButNotWorkbenchAsset } from '../../WorkbenchButton/util';
import { useInitialInfoStore } from '@src/emp-3.0/store/useInitialInfo';

const IndividualMetric = () => {
  const navigate = useNavigate();
  const hasCreate = useCreatePermissions();

  const {
    // Tab related state
    openBulkAction,
    setOpenBulkAction,

    // Multiple selection operation
    selectedList,
    setSelectedList,
    setRemoveInvalidSelectedList,

    // Search
    searchText,
    setSearchText,
    // Filter conditions
    showFilter,
    status,
    updateDate,
    deployDate,
    createdBy,
    updatedBy,
    metricsType,
    setStatus,
    setUpdateDate,
    setDeployDate,
    setCreatedBy,
    setUpdatedBy,
    setMetricsType,

    // Sort state
    sort,
    setSort,

    // Total Count related
    totalCount,
    setTotalCount,
    resetFilter,
    setShowFilter,
  } = useMetricsManagementStore((state) => state);
  const { formMode, setFormMode } = useWorkflowStore((state) => state);

  const [metricId, setMetricId] = useState<number | undefined>(undefined);
  const [dataSource, setDataSource] = useState<any[]>([]);
  const { workbenchMode } = useInitialInfoStore();
  // request
  const { data: allMappedEvents, isFetching: isLoadingAllMappedEvents } =
    useGetMappedEventsWithOutOfBox();
  const { data: metricList, isFetching: isLoadingMetricList } = useMetricManagementList();
  const { data: metric } = useGetMetricById({ id: metricId });
  const { getMappedEventNameById, refetch: refetchEventNames } = useGetEventNameById();
  const { mutate: deployMetrics, isLoading: isLoadingDeploy } = useDeploy();
  const { mutate: undeployMetrics, isLoading: isLoadingUndeploy } = useUndeploy();
  const { mutate: deleteMetrics, isLoading: isLoadingDelete } = useDeleteMetric();
  const { mutate: deleteVersions } = useDeleteVersion();

  const isLoading = useMemo(
    () =>
      isLoadingAllMappedEvents ||
      isLoadingMetricList ||
      isLoadingDeploy ||
      isLoadingUndeploy ||
      isLoadingDelete,
    [
      isLoadingAllMappedEvents,
      isLoadingDeploy,
      isLoadingMetricList,
      isLoadingUndeploy,
      isLoadingDelete,
    ],
  );
  const { createdByOptions, updatedByOptions } = useMemo(
    () => generateSelectOptions(metricList, DEFAULT_SELECT_VALUE),
    [metricList],
  );

  const eventIdToNameMap = useMemo(
    () =>
      fromPairs((allMappedEvents?.data ?? []).map((event) => [event.event_id, event.event_name])),
    [allMappedEvents?.data],
  );

  const transformedData = useMemo(() => {
    if (isEmpty(eventIdToNameMap)) return metricList ?? [];
    return (metricList ?? []).map((item) => ({
      ...item,
      versions: item.versions.map((version) => ({
        ...version,
        related_event: version.related_event.map((event_id) =>
          getMappedEventNameById(event_id),
        ) as string[],
      })),
    }));
  }, [getMappedEventNameById, eventIdToNameMap, metricList]);

  const filteredSortedData = useMemo(
    () =>
      getFilteredSortedData<IMetricsList>({
        dataList: transformedData,
        filterParams: {
          status,
          updateDate,
          deployDate,
          createdBy,
          updatedBy,
          metricsType,
        },
        searchParams: {
          searchText,
          supportSearchFields: supportSearchFields.filter((it) => it !== 'related_metrics'),
          dateFields,
        },
        sortParams: sort,
      }),
    [
      createdBy,
      deployDate,
      searchText,
      sort,
      status,
      transformedData,
      updateDate,
      updatedBy,
      metricsType,
    ],
  );

  const tableData = useMemo(
    () =>
      flatMap<IMetricsList, any>(filteredSortedData, (element, index) =>
        element.versions.map((version) => {
          const id = `${element.metric_id}_${version.version_id}`;
          const is_multi_version = element.versions.length > 1;
          const related_event = version.related_event.map((event_id) =>
            getMappedEventNameById(event_id),
          );
          const metrics_type = metricTypeMapping[version.metrics_type] ?? version.metrics_type;
          return {
            ...version,
            id,
            related_event,
            metric_id: element.metric_id,
            metrics_type,
            row_number: index,
            is_multi_version,
            expandedState: {
              // expanded object key is column dataKey
              related_event: true,
              associated_dimension_names: true,
            },
          };
        }),
      ),
    [getMappedEventNameById, filteredSortedData],
  );

  useEffect(() => {
    setDataSource((prev) => {
      if (!isEqual(prev, tableData)) {
        return cloneDeep(tableData);
      }
      return prev;
    });
  }, [tableData]);

  useEffect(() => {
    setRemoveInvalidSelectedList(dataSource);
  }, [setRemoveInvalidSelectedList, dataSource]);

  const handleNameClick = ({ rowData }) => {
    setMetricId(rowData.metric_id);
  };

  const handleToggleExpanded = useCallback(
    ({ rowData }) => {
      dataSource.forEach((d) => {
        if (d.id === rowData?.id) d.expandedState = rowData?.expandedState;
      });
      setDataSource([...dataSource]);
    },
    [dataSource],
  );

  const handleCloneClick = ({ rowData }) => {
    setFormMode(EFormMode.CLONE);
    setMetricId(rowData.metric_id);
    const url = `/workflow/metric/new?clone_id=${rowData.metric_id}`;
    navigate(url);
  };

  const handleDeployMetrics = useCallback(
    ({ rowData }: { rowData: any }) => {
      if (rowData) {
        handleAction({
          action: deployMetrics,
          params: {
            resource: 'metrics',
            payload: {
              deploy_all: false,
              ids: [rowData.metric_id],
            },
          },
          successMessage: 'Deploy Successfully!',
        });
      }
    },
    [deployMetrics],
  );

  const handleUndeployMetrics = useCallback(
    ({ rowData }: { rowData: any }) => {
      if (rowData) {
        handleAction({
          action: undeployMetrics,
          params: {
            resource: 'metrics',
            payload: {
              ids: [rowData.metric_id],
            },
          },
          successMessage: 'Undeploy Successfully!',
        });
      }
    },
    [undeployMetrics],
  );

  const handleDeleteMetrics = useCallback(
    ({ rowData }: { rowData: any }) => {
      if (rowData) {
        const isSubVersion = isMultiVersion(rowData) && isDraftVersion(rowData);
        if (isSubVersion) {
          handleAction({
            action: deleteVersions,
            params: { type: DeleteVersionType.metrics, ids: [rowData.version_id] },
            successMessage: 'Delete Successfully!',
          });
        } else {
          handleAction({
            action: deleteMetrics,
            params: { id: rowData.metric_id },
            successMessage: 'Delete Successfully!',
          });
        }
      }
    },
    [deleteMetrics, deleteVersions],
  );

  const columns = useMemo<ColumnShape<any>[]>(() => {
    const columnDefinitions: any[] = [
      {
        columnType: 'checkbox',
        dataKey: 'metric_name',
        selectedList,
        onChange: ({ selectedList }) => {
          setOpenBulkAction(selectedList.length > 0);
          setSelectedList(selectedList);
        },
        data: dataSource,
        disable: (rowData) => workbenchModeTrueButNotWorkbenchAsset(rowData?.is_beta),
      },
      {
        columnType: 'number',
      },
      {
        columnType: 'name',
        title: 'Metrics',
        key: 'metrics',
        dataKey: 'metric_name',
        sort,
        onSortChange: setSort,
        onClick: handleNameClick,
      },
      {
        columnType: 'status',
        options: statusOptions,
        value: status,
        onChange: setStatus,
        showFilter,
        sort,
        onSortChange: setSort,
      },
      {
        columnType: 'common',
        title: 'Type',
        key: 'metrics-type',
        dataKey: 'metrics_type',
        width: 100,
        onChange: setMetricsType,
        value: metricsType,
        showFilter,
        options: metricTypeOptions,
        sort,
        onSortChange: setSort,
      },
      {
        columnType: 'list',
        title: 'Mapped Event Names',
        key: 'mapped-event-names',
        dataKey: 'related_event',
        onClick: handleToggleExpanded,
      },
      {
        columnType: 'list',
        title: 'Associated Dimension',
        key: 'associated_dimension_names',
        dataKey: 'associated_dimension_names',
        width: 230,
        onClick: handleToggleExpanded,
      },
      {
        columnType: 'common',
        title: 'Created By',
        key: 'created by',
        dataKey: 'created_by',
        options: createdByOptions,
        value: createdBy,
        onChange: setCreatedBy,
        showFilter,
        sort,
        onSortChange: setSort,
      },
      {
        columnType: 'common',
        title: 'Updated By',
        key: 'updated by',
        dataKey: 'updated_by',
        options: updatedByOptions,
        value: updatedBy,
        onChange: setUpdatedBy,
        showFilter,
        sort,
        onSortChange: setSort,
      },
      {
        columnType: 'date',
        title: 'Updated At',
        key: 'updated at',
        dataKey: 'updated_at',
        value: updateDate,
        onChange: setUpdateDate,
        showFilter,
        sort,
        onSortChange: setSort,
      },
      {
        columnType: 'date',
        title: 'Deployed At',
        key: 'deployed at',
        dataKey: 'deployed_at',
        value: deployDate,
        onChange: setDeployDate,
        showFilter,
        sort,
        onSortChange: setSort,
      },
    ];
    if (hasCreate) {
      columnDefinitions.push({
        columnType: 'actions',
        dataKey: 'metric_name',
        type: METRICS,
        cloneVisible: true,
        onClone: handleCloneClick,
        onDeploy: handleDeployMetrics,
        onUndeploy: handleUndeployMetrics,
        onDelete: handleDeleteMetrics,
      });
    }

    return columnDefinitions.map((config) => generateColumn(config));
  }, [
    selectedList,
    dataSource,
    sort,
    setSort,
    status,
    setStatus,
    showFilter,
    createdByOptions,
    createdBy,
    setCreatedBy,
    updatedByOptions,
    updatedBy,
    setUpdatedBy,
    updateDate,
    setUpdateDate,
    deployDate,
    setDeployDate,
    hasCreate,
    setOpenBulkAction,
    setSelectedList,
    handleCloneClick,
    handleDeployMetrics,
    handleUndeployMetrics,
    handleDeleteMetrics,
    workbenchMode,
  ]);

  useEffect(() => {
    refetchEventNames(); // Each time navigate to the management page, pull the latest mapped event list for id to name mapping.
    setFormMode(EFormMode.VIEW);
  }, []);

  useEffect(() => {
    setTotalCount(metricList?.length ?? 0);
  }, [metricList?.length, setTotalCount]);

  useEffect(() => {
    if (metric && metricId && formMode === EFormMode.VIEW) {
      const url = `/metric-builder/metric/${metricId}`;
      navigate(url);
    }
  }, [navigate, metric, metricId, formMode]);
  const handleSearch = (value) => {
    setSearchText(value);
  };
  const handleShowFilterChange = (value: boolean) => {
    if (!value) {
      resetFilter();
    }
    setShowFilter(value);
  };

  return (
    <ManagementTable
      loading={isLoading}
      data={dataSource}
      columns={columns}
      rowKey="id"
      estimatedRowHeight={50}
      ignoreFunctionInColumnCompare={false}
      header={
        <SearchFilterPanel
          leftComp={
            <>
              <BulkAction
                resource={METRICS}
                data={selectedList}
                show={openBulkAction}
                onClose={() => {
                  setOpenBulkAction(false);
                  setSelectedList([]);
                }}
              />
              {!openBulkAction && (
                <div style={{ fontWeight: 400 }}>{`Total ${totalCount} Individual Metrics`}</div>
              )}
            </>
          }
          searchValue={searchText}
          onSearch={handleSearch}
          filtersVisible={true}
          showFilters={showFilter}
          onShowFilterChange={handleShowFilterChange}
        />
      }
      rowClassName={({ rowData }) => {
        return classnames({
          [managementTableStyles.tableRowActive]: selectedList?.find(
            (item) => item.id === rowData.id,
          ),
        });
      }}
    />
  );
};

export default IndividualMetric;
